import {initWebsocket, websocketDisconnected} from '@services/common/common.action';
import {commonWebSocket} from '@services/common/common.constants';
import {logger} from '@utils';
import {delay, SagaIterator} from 'redux-saga';
import {all, call, fork, put, race, select, take} from 'redux-saga/effects';
import {privatRoutesPathes} from '@router/router.constants';
import * as UserActions from './user.actions';
import * as userHttp from './user.http';
import * as userWebsocket from './user.websocket';
import TranslateNew from '@translate/TranslateNew';
import { IUserCreate } from '@app-types/api/user.types';
import { setAuthHeader } from '@clients/http.client';
import storage from 'redux-persist/lib/storage'; // same backend as your persist config
import { getPersistor } from 'src/persistor'; // adjust path to your store folder
import { resetApp, resetExceptSession } from '@services/reset';        // adjust path
import { logFirebaseAnalyticsEvent } from '@services/firebase/firebaseAnalytics';
import { clearFirebaseOnDeviceMeasurement, initializeFirebaseOnDeviceMeasurement } from '@services/firebase/firebaseOnDeviceMeasurement';
import { uploadFailureMessage } from '@services/file/upload.errors';
import { getConnectionErrorMessage, isNetworkFailureOrTimeout } from '@services/status/maintenance-status';
import { savePassword, clearPasswordManagerSession } from '@helpers/passwordManager';
import { GMAIL_CONNECT_RETURN_PATH, getSafeReturnPath } from '@router/safeReturn';
import { markNativeOriginStorageLogout } from '@helpers/nativeOriginStorageMigration';
import { getSelectedFamilyResource, hasAuthToken } from './user.utils';

export const PROTOCOL_VERSION = 114;

export function* activateEmailAddress(action: any): SagaIterator {
  const { accountId, familyId, requestId } = action.payload as UserActions.IEmailAddressActivation;
  const state: any = yield select();
  const pending = state.connection.emailActivation;
  if (!pending?.pending || pending.requestId !== requestId) { return; }
  let failed = true;
  try {
    if (state.user.session?.data?.id === accountId && getSelectedFamilyResource(state.user.session)?.id === familyId) {
      yield call(userWebsocket.activateEmailAddress, requestId, familyId);
      const result: any = yield race({
        reply: take((event: any) => event.type === commonWebSocket.special.REQUEST_ID && event.payload?.requestId === requestId),
        disconnected: take(websocketDisconnected),
        timeout: call(delay, 15000),
      });
      failed = !result.reply || Boolean(result.reply.payload.hasError);
    }
  } catch (error) {
    // A disconnected or switched socket must not queue activation for another family.
    failed = true;
  } finally {
    yield put(UserActions.emailAddressActivationFinished({ requestId, failed }));
  }
}

// Switches away only once the server has confirmed, since the switch closes this group's socket.
// leaveFamily throws instead of queuing, so the delete is never sent over another group's socket.
export function* deleteGroup(action: any): SagaIterator {
  const { accountId, familyId, requestId } = action.payload as UserActions.IDeleteGroup;
  const tn = TranslateNew.getInstance();
  const state: any = yield select();
  const session = state.user.session;
  const resources: any[] = Array.isArray(session?.data?.resources) ? session.data.resources : [];
  const deletedGroup = getSelectedFamilyResource(session);
  const nextIndex = resources.findIndex((resource: any) => resource?.id !== familyId);
  if (session?.data?.id !== accountId || deletedGroup?.id !== familyId || nextIndex < 0) {
    yield put(UserActions.displayErrorMessage(tn.lang.deleteGroupFailed));
    return;
  }

  try {
    yield call(userWebsocket.leaveFamily, requestId, familyId, accountId);
    const result: any = yield race({
      reply: take((event: any) => event.type === commonWebSocket.special.REQUEST_ID && event.payload?.requestId === requestId),
      disconnected: take(websocketDisconnected),
      timeout: call(delay, 15000),
    });
    if (!result.reply || result.reply.payload.hasError) {
      yield put(UserActions.displayErrorMessage(tn.lang.deleteGroupFailed));
      return;
    }
  } catch (error) {
    yield put(UserActions.displayErrorMessage(tn.lang.deleteGroupFailed));
    return;
  }

  yield call(switchFamilySaga, UserActions.selectFamily(nextIndex));
  // The session still lists the deleted group. Queued until the new socket is in; the server's list leaves it out.
  yield call(userWebsocket.reloadResources);
  yield put(UserActions.displaySuccessMessage(tn.lang.groupDeleted(deletedGroup.familyName)));
}

// One deletion at a time: a second confirm while the first waits for the server is dropped.
export function* watchDeleteGroup(): SagaIterator {
  while (true) {
    const action = yield take(UserActions.deleteGroup);
    yield call(deleteGroup, action);
  }
}

function* offerPasswordSave(email: string, password: string): SagaIterator {
  const result: any = yield call(savePassword, email, password);
  if (result === 'failed') {
    yield put(UserActions.displayErrorMessage(TranslateNew.getInstance().lang.passwordSaveFailed));
  }
}

function* resumePersistedAuthState(): SagaIterator {
  const persistor = getPersistor();
  if (persistor && typeof persistor.persist === "function") {
    yield call([persistor, persistor.persist]);
  }
}

function* flushPersistedAuthState(successMessage: string): SagaIterator {
  const persistor = getPersistor();
  if (!persistor) {
    console.log("No persistor available");
    return;
  }

  try {
    if (typeof persistor.persist === "function") {
      yield call([persistor, persistor.persist]);
    }
    yield call([persistor, persistor.flush]);
    console.log(successMessage);
  } catch (err) {
    logger.warn(err);
    console.log("Could not flush persistor");
  }
}

export function* userTokenUpdate(action: any): SagaIterator {
  const token = action.payload?.userToken;

  if (typeof token !== "string" || token.length < 20) {
    logger.warn("Ignoring invalid USER_TOKEN_UPDATE payload.");
    return;
  }

  setAuthHeader(token);
  yield call(flushPersistedAuthState, "Persistor flushed refreshed auth token.");
}


export function* loginUser(action: any): SagaIterator {
  try {
    const returningToGmail = getSafeReturnPath(action.payload?.meta?.history?.location?.state)
      === GMAIL_CONNECT_RETURN_PATH;
    const { data: user }:any = yield call(userHttp.loginUser, action.payload.data);
    const appToken:string | undefined = yield select((state: any) => state.user.appToken);
    const resources:any[] | undefined = Array.isArray(user?.resources) ? user.resources : undefined;

    if (!user?.authorization || !resources || (!returningToGmail && resources.length === 0)) {
      // Never log the login response: it contains the bearer authorization and
      // may contain other account data. A Gmail return can authenticate without
      // a family resource because inspect is the API that supplies its targets.
      console.warn("Login response is missing required session data.");
      yield put(UserActions.displayErrorMessage("Login failed. contact support."));
      return;
    }

    let selectedFamily:any = yield select((state: any) => state.user.session.selectedFamily);

    if (!Number.isInteger(selectedFamily) || selectedFamily < 0 || selectedFamily >= resources.length) {
      selectedFamily = 0;
    }

    const selectedResource = resources[selectedFamily];

    if (!returningToGmail && !selectedResource?.familyURL) {
      console.warn("Login response is missing a family connection URL.");
      yield put(UserActions.displayErrorMessage("Login failed: missing family URL."));
      return;
    }

    

    if (!returningToGmail && selectedResource.protocolVersion > PROTOCOL_VERSION) {
      console.log("Client protocol version too low:", selectedResource.protocolVersion, ">", PROTOCOL_VERSION);
      yield put(UserActions.displayErrorMessage("You have to download a new version of this app!"));
      return;
    }

    yield call(resumePersistedAuthState);
    yield put(UserActions.loginSuccess(user));
    yield call(flushPersistedAuthState, "Persistor flushed successfully.");

    if (!returningToGmail) {
      yield call(initializeFirebaseOnDeviceMeasurement, user?.id, user?.email);
      yield call(logFirebaseAnalyticsEvent, "login", {method: "email"}, user?.id);

      yield put(initWebsocket({
        socketURL: selectedResource.familyURL,
        bootstrapTime:0,
        appToken,
        subscribeData: {
          authorization: user.authorization
        }
      }));
    }
    
    if (action.payload.meta?.saveCredentials !== false) {
      yield fork(offerPasswordSave, action.payload.data.email, action.payload.data.password);
    }
  } catch (error) {
    let errorMessage: string;
    if (error.response && error.response.statusText ) {
      const errorText:string = error.response.statusText;

      if ( errorText === "Email not found")  {
        errorMessage = "Sorry there is no account what that email!";
      } else if ( errorText === "Authorization Failed")  {
        errorMessage = "Login failed! But we sent you a magic link to the email you entered, try clicking it!";
      } else {
        errorMessage = "Login failed! "+error.response.statusText;
      }
    } else if (error.message ) {
      errorMessage = "Login failed! ("+error.message+", could not connect to server)";
    } else {
      errorMessage = "Login failed! (unknown error)";
    }

    if (isNetworkFailureOrTimeout(error)) {
      // redux-saga 0.16 types the value injected back into SagaIterator as
      // undefined even though this async function resolves to a string.
      errorMessage = (yield call(getConnectionErrorMessage, errorMessage)) as any;
    }

    yield put(UserActions.displayErrorMessage(errorMessage));
  }
}

export function* reloadResources(action: any): SagaIterator {
  try {
    userWebsocket.reloadResources();
    yield put(UserActions.displaySuccessMessage("Updated available families"));
  } catch (error) {
    yield put(UserActions.displayErrorMessage("Could not renew resources: "+error));
  }
}

export function* loginMagicUser(action: any): SagaIterator {
  // Logging in over a live session would swap accounts without resetting its data.
  if (yield select(hasAuthToken)) {
    return;
  }
  try {
    const { data: user }:any = yield call(userHttp.loginMagicUser, action.payload);
    const appToken:string | undefined = yield select((state: any) => state.user.appToken);

    if (!user?.authorization || !Array.isArray(user.resources) || user.resources.length === 0) {
      console.log("Magic login failed. contact support.", user);
      yield put(UserActions.displayErrorMessage("Login failed. contact support."));
      return;
    }

    let selectedFamily:any = yield select((state: any) => state.user.session.selectedFamily);

    if (!Number.isInteger(selectedFamily) || selectedFamily < 0 || selectedFamily >= user.resources.length) {
      selectedFamily = 0;
    }

    const selectedResource = user.resources[selectedFamily];

    if (!selectedResource?.familyURL) {
      console.log("No family URL for selected family:", selectedFamily, user.resources);
      yield put(UserActions.displayErrorMessage("Login failed: missing family URL."));
      return;
    }

    if (selectedResource.protocolVersion > PROTOCOL_VERSION) {
      console.log("Client protocol version too low:", selectedResource.protocolVersion, ">", PROTOCOL_VERSION);
      yield put(UserActions.displayErrorMessage("You have to download a new version of this app!"));
      return;
    }

    yield call(resumePersistedAuthState);
    yield put(UserActions.loginSuccess(user));
    yield call(flushPersistedAuthState, "Flushed persistor with user data.");

    yield call(initializeFirebaseOnDeviceMeasurement, user?.id, user?.email);
    yield call(logFirebaseAnalyticsEvent, "login", {method: "magic_link"}, user?.id);

    yield put(initWebsocket({
      socketURL: selectedResource.familyURL,
      bootstrapTime:0,
      appToken,
      subscribeData: {
        authorization: user.authorization
      }
    }));
    
  } catch (error) {
    let errorMessage: string;
    if (error.response && error.response.statusText ) {
      errorMessage = "The magic token is not valid! "+error.response.statusText;
    } else {
      errorMessage = "The magic token is not valid!";
    }

    if (isNetworkFailureOrTimeout(error)) {
      errorMessage = (yield call(getConnectionErrorMessage, errorMessage)) as any;
    }

    yield put(UserActions.displayErrorMessage(errorMessage));
  }
}


export function* signupUser(action: any): SagaIterator {
  try {
    const appToken:string | undefined = yield select((state: any) => state.user.appToken);

    const body = {
      ...action.payload,
    };

    const { data: user }:any = yield call(userHttp.createUser, body);

    if (!user?.authorization || !Array.isArray(user.resources) || user.resources.length === 0) {
      console.log("Signup failed. contact support.", user);
      yield put(UserActions.displayErrorMessage("Could not create account: missing session data."));
      return;
    }

    if (user.resources[0].protocolVersion > PROTOCOL_VERSION) {
      yield put(UserActions.displayErrorMessage("You have to download a new version of this app!"));
      return;
    }

    yield call(resumePersistedAuthState);
    yield put(UserActions.createUserSuccess(user));
    yield call(initializeFirebaseOnDeviceMeasurement, user?.id, user?.email);
    yield call(logFirebaseAnalyticsEvent, "sign_up", {method: "email"}, user?.id);
    yield call(flushPersistedAuthState, "Persistor flushed successfully.");

    yield put(initWebsocket({
      socketURL: user.resources[0].familyURL,
      bootstrapTime:0,
      appToken,
      subscribeData: {
        authorization: user.authorization
      }
    }));

    yield fork(offerPasswordSave, body.email, body.newPassword);
    
  } catch (error) {
    let errorMsg:string = error.message;
    if (error.response && error.response.request && error.response.request.statusText)
    {
      errorMsg = "Could not create account: "+error.response.request.statusText;
    }
    if (errorMsg && errorMsg.indexOf("Duplicate entry") > -1) {
      errorMsg =  TranslateNew.getInstance().lang.signUpEmailUsed;
    }
    yield put(UserActions.displayErrorMessage(errorMsg));
  }
}

export function* getInvitedUser(action: any): SagaIterator {
  // The invited account replaces session.data, which would drop a live session's token.
  if (yield select(hasAuthToken)) {
    return;
  }
  try {
    const { data: user }:any = yield call(userHttp.getInvitedUser, action.payload);
    console.log("Invited user data: ", user);
    yield put(UserActions.createUserSuccess(user));
  } catch (error) {
    yield put(UserActions.displayErrorMessage("Could not find the invitation!"));
  }
}

export function* storeCampaignToken(action: any): SagaIterator {
  const payload = action.payload;
  const token = typeof payload === 'string' ? payload : payload?.token;
  const onComplete = typeof payload?.onComplete === 'function' ? payload.onComplete : undefined;

  try {
    if (token) {
      yield call(userHttp.storeCampaignToken, token);
    }
  } catch (error) {
    yield put(UserActions.displayErrorMessage("Could not validate campaign!"));
  } finally {
    if (onComplete) {
      onComplete();
    }
  }
}

export function* storeInviteToken(action: any): SagaIterator {
  try {
    yield call(userHttp.storeInviteToken, action.payload);
  } catch (error) {
    yield put(UserActions.displayErrorMessage("Could not store invite token!"));
  }
}

export function* getInviteToken(action: any): SagaIterator {
  try {
    const { data: token }:any = yield call(userHttp.getInviteToken, action.payload);
    yield put(UserActions.getInviteTokenSuccess(token.token));
  } catch (error) {
    console.error("Could not figure out who invited you!");
  }
}

export function* getMunicipality(action: any): SagaIterator {
  try {
    userWebsocket.locationCheck(action.payload);
  } catch (error) {
    yield put(UserActions.displayErrorMessage("Could not check location: "+error));
  }
}

export function* getCampaignToken(action: any): SagaIterator {
  
  try {
    const { data: token }:any = yield call(userHttp.getCampaignToken, action.payload);
    yield put(UserActions.getCampaignTokenSuccess(token));
  } catch (error) {
    // yield put(UserActions.displayErrorMessage("Could not find out what link you clicked to get here!"));
  }
}

export function* claimSpecialOffer(action: any): SagaIterator {
  
  try {
    const reponse = yield call(userHttp.specialOffer, action.payload.familyId, action.payload.accountId, action.payload.offer);
  } catch (error) {
    yield put(UserActions.displayErrorMessage("Could not claim special offer!"));
  }
}

export function* signupInvite(action: any): SagaIterator {
  try {
    const appToken:string | undefined = yield select((state: any) => state.user.appToken);

    const payloadData:IUserCreate = action.payload;
    const { data: user }:any = yield call(userHttp.signupInvite, payloadData);

    if (!user?.authorization || !Array.isArray(user.resources) || user.resources.length === 0) {
      console.log("Signup invite failed. contact support.", user);
      yield put(UserActions.displayErrorMessage("Could not create account: missing session data."));
      return;
    }

    if (user.resources[0].protocolVersion > PROTOCOL_VERSION) {
      yield put(UserActions.displayErrorMessage("You have to download a new version of this app!"));
      return;
    }

    yield call(resumePersistedAuthState);
    yield put(UserActions.createUserSuccess(user));
    yield call(initializeFirebaseOnDeviceMeasurement, user?.id, user?.email);
    yield call(logFirebaseAnalyticsEvent, "sign_up", {method: "email"}, user?.id);

    yield call(flushPersistedAuthState, "Persistor flushed successfully.");

    yield put(initWebsocket({
      socketURL: user.resources[0].familyURL,
      bootstrapTime:0,
      appToken,
      subscribeData: {
        authorization: user.authorization
      }
    }));

    yield fork(offerPasswordSave, payloadData.email, payloadData.newPassword);

  } catch (error) {
    let errorMsg:string = error.message;
    if (error.response && error.response.request && error.response.request.statusText)
    {
      errorMsg = error.response.request.statusText;
    }
    yield put(UserActions.displayErrorMessage("Could not create account: "+errorMsg));
  }
}
const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

export function* switchFamilySaga(action: any): SagaIterator {
  try {
    const newIndex: number = Number(action.payload);

    const state: any = yield select();               // ← always defined
    const session = state?.user?.session;
    const appToken:string | undefined = state?.user?.appToken;

    if (!Number.isInteger(newIndex) || newIndex < 0) {
      yield put(UserActions.displayErrorMessage('Could not switch family (invalid selection).'));
      return;
    }

    if (!Array.isArray(session?.data?.resources) || session.data.resources.length === 0) {
      yield put(UserActions.displayErrorMessage('Could not switch family (no session).'));
      return;
    }

    const resources = session.data.resources;
    const target: any = resources[newIndex];

    if (!target) {
      yield put(UserActions.displayErrorMessage('Could not switch family (invalid selection).'));
      return;
    }

    if (target.protocolVersion > PROTOCOL_VERSION) {
      yield put(UserActions.displayErrorMessage("You have to download a new version of this app!"));
      return;
    }

    if (typeof target.familyURL !== "string" || target.familyURL.length === 0) {
      yield put(UserActions.displayErrorMessage('Could not switch family (missing family URL).'));
      return;
    }

    // Close current socket (safe if already closed)
    userWebsocket.close();

    // Keep using the same auth token
    const authorization: string | undefined = session?.data?.authorization;
    if (authorization) {
      setAuthHeader(authorization);
    }

    // Preserve session with updated selectedFamily
    const preservedSession = { ...session, selectedFamily: newIndex };

    // Reset everything except preserved session data and the stored app token.
    yield put(resetExceptSession({ session: preservedSession, appToken }));

    yield call(sleep, 100);

    // Reconnect websocket to the new family
    yield put(
      initWebsocket({
        socketURL: target.familyURL,
        bootstrapTime: 0,
        appToken,
        subscribeData: { authorization },
      })
    );

    if (target.familyName) {
      yield put(UserActions.displaySuccessMessage(`Switched to ${target.familyName}`));
    }
  } catch (err) {
    yield put(UserActions.displayErrorMessage('Could not switch family.'));
  }
}

export function* uploadOfflineEventAnalysis(action: any): SagaIterator {
  try {
    yield call(userHttp.uploadOfflineEventAnalysis, action.payload);
  } catch (error) {
    yield put(UserActions.displayErrorMessage(uploadFailureMessage(error, "Could not upload image! ")));
  }
}

export function* changePassword(action: any): SagaIterator {
  try {

    userWebsocket.changePassword(action.payload);

  } catch (error) {
    logger.info(error);
  }
}

export function* updateEmail(action: any): SagaIterator {
  try {

    userWebsocket.updateEmail(action.payload);

  } catch (error) {
    logger.info(error);
  }
}

export function* eraseAccount(action: any): SagaIterator {
  try {

    userWebsocket.eraseAccount();

  } catch (error) {
    logger.info(error);
  }
}

function* cleanLogoutStep(cleanup: () => any): SagaIterator {
  try {
    yield call(cleanup);
  } catch (error) {
    // Each independent cleanup still runs if another storage/plugin API fails.
    logger.warn('A logout cleanup step failed.');
  }
}

export function* logout(action: any): SagaIterator {
  // Notify the server before detaching the socket. No response is required for
  // local logout, including when this is recovery from an offline login.
  yield call(cleanLogoutStep, userWebsocket.logout);
  yield call(cleanLogoutStep, userWebsocket.close);
  setAuthHeader();
  clearFirebaseOnDeviceMeasurement();
  // A local-only marker prevents stale backups from reviving this session,
  // including if the first-launch marker could not be written at the time.
  yield call(cleanLogoutStep, markNativeOriginStorageLogout);

  const persistor = getPersistor();
  if (persistor) {
    yield call(cleanLogoutStep, () => persistor.pause());
  }
  yield put(resetApp());

  if (persistor) {
    yield call(cleanLogoutStep, () => persistor.flush());
    yield call(cleanLogoutStep, () => persistor.purge());
  }
  yield call(cleanLogoutStep, () => storage.removeItem('persist:root'));
  // Direct fallback also works when redux-persist's storage adapter fails.
  yield call(cleanLogoutStep, () => window.localStorage.removeItem('persist:root'));

  if (persistor && typeof persistor.persist === 'function') {
    yield call(cleanLogoutStep, () => persistor.persist());
  }

  // Native work continues if its callbacks are delayed, but must not prevent
  // the user from trying to sign in again after local credentials are removed.
  const nativeCleanup: any = yield race({
    cleared: all([
      call(cleanLogoutStep, clearPasswordManagerSession),
    ]),
    timeout: call(delay, 2000),
  });
  if (nativeCleanup.timeout) logger.warn('Native logout cleanup is taking longer than expected.');
}

export function* onboarding(action: any): SagaIterator {
  try {

    userWebsocket.onboarding(action.payload);

  } catch (error) {
    logger.info(error);
  }
}

export function* updateFamilyColors(action: any): SagaIterator {
  try {

    userWebsocket.updateFamilyColors(action.payload);

  } catch (error) {
    logger.info(error);
  }
}
export function* updateCalendarSettings(action: any): SagaIterator {
  try {
    const session: any = yield select((state: any) => state.user.session);
    if ((action.payload.accountId !== undefined && action.payload.accountId !== session?.data?.id)
      || (action.payload.familyId !== undefined && action.payload.familyId !== getSelectedFamilyResource(session)?.id)) {
      return;
    }
    userWebsocket.updateCalendarSettings(action.payload);

  } catch (error) {
    logger.info(error);
  }
}
export function* updateCountry(action: any): SagaIterator {
  try {
    userWebsocket.storeCountry(action.payload);
  } catch (error) {
    logger.info(error);
  }
}
export function* setOnlyWeeksOf(action: any): SagaIterator {
  try {
    userWebsocket.setOnlyWeeksOf(action.payload);
  } catch (error) {
    logger.info(error);
  }
}
export function* updateCalendarSettingsSynch(action: any): SagaIterator {
  try {

    userWebsocket.updateCalendarSettingsSynch(action.payload);

  } catch (error) {
    logger.info(error);
  }
}

export function* updateCalendarSettingsExclude(action: any): SagaIterator {
  try {

    userWebsocket.updateCalendarSettingsExclude(action.payload);

  } catch (error) {
    logger.info(error);
  }
}

export function* updateAppToken(action: any): SagaIterator {
  try {
    userWebsocket.updateAppToken(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

export function* toxicCheckLevel(action: any): SagaIterator {
  try {
    userWebsocket.toxicCheckLevel(action.payload);

  } catch (error) {
    logger.info(error);
  }
}

export function* disablePreviews(action: any): SagaIterator {
  try {
    userWebsocket.disablePreviews(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

export function* autoApproveSuggestions(action: any): SagaIterator {
  try {
    userWebsocket.autoApproveSuggestions(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

export function* darkModeSetting(action: any): SagaIterator {
  try {
    userWebsocket.darkModeSetting(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

/**
 * Saves a dark mode chosen on this device before the setting lived on the account, once the
 * account turns out to have none (see persistMigrations).
 */
export function* saveUnsavedDarkMode(): SagaIterator {
  try {
    const user: any = yield select((state: any) => state.user);
    if (user && user.darkModeUnsaved === true && typeof user.darkMode === 'boolean') {
      userWebsocket.darkModeSetting(user.darkMode);
    }
  } catch (error) {
    logger.info(error);
  }
}

export function* updateLanguage(action: any): SagaIterator {
  try {
    userWebsocket.updateLanguage(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

export function* updateDeviceData(action: any): SagaIterator {
  try {
    userWebsocket.deviceData(action.payload);
  } catch (error) {
    logger.info(error);
  }
}

export function* reportProblem(action: any): SagaIterator {
  try {
    userWebsocket.reportProblem(action.payload);
  } catch (error) {
    logger.info(error);
  }
}


export function* forgotPassword(action: any): SagaIterator {
  try {
    const { data }:any = yield call(userHttp.resetPassword, action.payload.data);
    logger.info("Forgot password response: ", data);
    // yield put(UserActions.forgotPasswordSuccess(data));
  } catch (error) {
    logger.info(error);
    let errorMessage = "Sorry there is no account what that email!";
    if (isNetworkFailureOrTimeout(error)) {
      errorMessage = (yield call(getConnectionErrorMessage, errorMessage)) as any;
    }
    yield put(UserActions.displayErrorMessage(errorMessage));
  }
}

export function* checkServerStatus(action: any): SagaIterator {
  const currentErrorMessage = typeof action.payload === 'string' && action.payload
    ? action.payload
    : "Cannot connect to the server. Try turning off your WiFi if this persists.";
  const errorMessage: string = (yield call(getConnectionErrorMessage, currentErrorMessage)) as any;
  yield put(UserActions.displayErrorMessage(errorMessage));
}

export function* inviteParent(action: any): SagaIterator {
  try {
    yield call(userWebsocket.inviteParent, action.payload);
  } catch (error) {
    logger.info(error);
  }
}



// WEBPACK FOOTER //
// ./src/services/user/user.saga.ts